import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  DOCUMENT
} from '@angular/core';

import { TranslateDirective } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { fromEvent, Subject, Subscription } from 'rxjs';

import { Menu, MenuPanelItem } from '../../domain/menu.domain';
import { ViewportRuler } from '../../services/viewport-ruler.service';
import { MenuService } from '../../services/menu/menu.service';
import { RouterBaseService } from '../../services/router/router-base.service';
import { ClientProfileService } from '../client-profile/client-profile.service';
import { navigationAnimation } from './menu.animation';
import { MenuLocalNavigationComponent } from './local-navigation/local-navigation.component';
import { MenuGlobalNavigationComponent } from './global-navigation/global-navigation.component';
import { StickyMenuContainerDirective } from './sticky-menu/sticky-menu-container.directive';
import { StickyMenuDirective } from './sticky-menu/sticky-menu.directive';
import { MenuTopNavigationComponent } from './top-navigation/top-navigation.component';

/**
 * The Menu Component
 *
 * This is the menu component
 */
@Component({
  selector: 'td-wb-menu',
  animations: [navigationAnimation],
  templateUrl: 'menu.component.html',
  styleUrls: ['menu.component.scss'],
  imports: [
    TranslateDirective,
    MenuLocalNavigationComponent,
    MenuTopNavigationComponent,
    MenuGlobalNavigationComponent,
    StickyMenuDirective,
    StickyMenuContainerDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuComponent implements OnInit, OnDestroy, AfterViewInit {

  private static readonly VIEWPORT_RULER_THROTTLE_TIME: number = 300;
  private static readonly LOGOUT_ITEM_ID: string = '4';
  @Input() isLocalMenuEnabled: boolean;
  @ViewChild(MenuLocalNavigationComponent) localNavigationComponent: MenuLocalNavigationComponent;
  @ViewChild(MenuGlobalNavigationComponent) globalNavigationComponent: MenuGlobalNavigationComponent;
  @ViewChild(MenuTopNavigationComponent) topNavigationComponent: MenuTopNavigationComponent;
  @ViewChild('content') content: ElementRef;

  /**
   * Menus to display
   */
  menu: Menu;

  /**
   * Logged in client name
   */
  clientName: string;

  /**
   * Logout item
   */
  logoutItem: MenuPanelItem;

  /**
   * Hidden - true to completely hide menu and footer, i.e., when the site is embedded
   */
  isHidden: boolean;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  transitionHookDeregisterFunction: Function;

  private contentRect: DOMRect;

  /**
   * A subscription to the [Client Profile Service's onClientNameChange observable]{@link ClientProfileService#onPreferredNameChange}
   */
  private clientNameSubscription: Subscription;

  private readonly destroy: Subject<void> = new Subject<void>();

  /**
   * The constructor of the component.
   *
   * @param menuService The menu service
   * @param clientProfileService The service which will give us the username to display
   * @param viewportRuler
   * @param routerService
   * @param changeDetectorRef
   * @param document
   */
  constructor(
    private readonly menuService: MenuService,
    private readonly clientProfileService: ClientProfileService,
    private readonly viewportRuler: ViewportRuler,
    private readonly routerService: RouterBaseService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document) {
    // Change detection on state navigation
    this.transitionHookDeregisterFunction = this.routerService.transitionOnStart(() => {
      this.changeDetectorRef.markForCheck();
    });
  }

  /**
   * This function initializes the list of menus and required variables for displaying the menu
   */
  ngOnInit(): void {
    this.isHidden = this.menuService.isHidden();
    this.menu = this.menuService.getMenu();
    this.clientName = this.clientProfileService.getFullName(true);
    this.clientNameSubscription = this.clientProfileService.onPreferredNameChange.subscribe((): void => {
      this.clientName = this.clientProfileService.getFullName(true);
      this.changeDetectorRef.markForCheck();
    });
    this.logoutItem = this.getItemById(MenuComponent.LOGOUT_ITEM_ID, this.menu.topNavigation);
    this.viewportRuler.change(MenuComponent.VIEWPORT_RULER_THROTTLE_TIME)
      .pipe(
        takeUntil(this.destroy)
      )
      .subscribe((): void => {
        this.getContentRect();
      });
  }

  ngOnDestroy(): void {
    this.transitionHookDeregisterFunction();
    if (this.clientNameSubscription !== undefined) {
      this.clientNameSubscription.unsubscribe();
    }
    this.destroy.next();
    this.destroy.complete();
  }

  ngAfterViewInit(): void {
    this.getContentRect();

    fromEvent<MouseEvent>(this.document, 'click')
      .pipe(
        takeUntil(this.destroy)
      )
      .subscribe((event: MouseEvent): void => {
        this.onClick(event);
      });
  }

  getActiveItemId(): string {
    return this.menuService.getCurrentLocalNavRootMenuId();
  }

  onGlobalNavigationSelected(): void {
    this.localNavigationComponent.close();
    this.topNavigationComponent.close();
  }

  onLocalNavigationSelected(): void {
    this.globalNavigationComponent.close();
    this.topNavigationComponent.close();
  }

  onTopNavigationSelected(): void {
    this.globalNavigationComponent.close();
    this.localNavigationComponent.close();
  }

  skipToContent(): false {
    const element = this.document.getElementById('td-wb-content');
    if (element !== undefined && element !== null) {
      element.focus();
    }
    return false;
  }

  private onClick(event: MouseEvent): void {
    // Guard target to an Element (handles cases like Text nodes)
    let el: Element | null = null;
    const tgt = event.target;
    if (tgt instanceof Element) {
      el = tgt;
    } else if ((tgt as Node | null)?.parentNode instanceof Element) {
      el = (tgt as Node).parentNode as Element;
    }

    if (!el) {
      // If we cannot resolve an element, treat as outside click and close all
      this.closeAll();
      return;
    }

    const clickedWithinMenuOrItem = el.closest('.td-wb-menu-item-content, .td-wb-menu-item') !== null;
    const clickedFocusableLink = el.closest('.td-wb-menu-item-content a') !== null;

    if (!clickedWithinMenuOrItem || clickedFocusableLink) {
      this.closeAll();
    }
  }

  /**
   * This function retrieves item by id
   *
   * @param id Id to retrieve
   * @param items Array of items to search from
   * @returns Returns item with id.
   */
  private getItemById(id: string, items: Array<MenuPanelItem>): MenuPanelItem {
    let i: number;
    for (i = 0; i < items.length; i++) {
      if (items[i].id === id) {
        return items[i];
      }
    }
    return undefined;
  }

  private closeAll(): void {
    if (this.globalNavigationComponent?.isOpen()) {
      this.globalNavigationComponent.close();
    }
    if (this.localNavigationComponent?.isOpen()) {
      this.localNavigationComponent.close();
    }
    if (this.topNavigationComponent?.isOpen()) {
      this.topNavigationComponent.close();
    }
  }

  private getContentRect(): void {
    if (this.content !== undefined) {
      this.contentRect = this.content.nativeElement.getBoundingClientRect();
      this.menuService.onMenuWidthChange(this.contentRect.width);
      this.changeDetectorRef.detectChanges();
    }
  }
}
